// @ts-nocheck
import { Box, Flex, useColorMode } from "@chakra-ui/react";
import { createFileRoute } from "@tanstack/react-router";
import { useState, useCallback, useRef, useEffect } from "react";
import { FiFileText } from "react-icons/fi";

import DocumentUploader from "@/components/Document/DocumentUploader";
import DocumentSchema from "@/components/Document/DocumentSchema";
import {
  DEFAULT_SCHEMA,
  type SchemaField,
} from "@/components/Document/DocumentSchema";
import { useDocumentStore } from "@/stores/documents";

const ExtractSchema = () => {
  const [currentSchema, setCurrentSchema] = useState<SchemaField[]>(() => {
    const savedSchema = localStorage.getItem("documentSchema");
    if (savedSchema) {
      try {
        const parsed = JSON.parse(savedSchema);
        return parsed;
      } catch (error) {
        console.error("저장된 스키마 로드 실패:", error);
        localStorage.removeItem("documentSchema");
        return DEFAULT_SCHEMA;
      }
    }
    return DEFAULT_SCHEMA;
  });
  const [selectedTemplate, setSelectedTemplate] = useState<string>(() => {
    const saved = localStorage.getItem("selectedTemplate");
    return saved || "user_defined";
  });
  const templateLoadCallbackRef = useRef<
    ((templateName: string) => void) | null
  >(null);

  const { pdfFile, pdfDocument, isDocumentLoaded } = useDocumentStore();

  // 컴포넌트 마운트 시 초기화
  useEffect(() => {
    // 초기 스키마를 localStorage에 저장하여 DocumentSchema와 동기화
    if (currentSchema && currentSchema.length > 0) {
      localStorage.setItem("documentSchema", JSON.stringify(currentSchema));
    }
  }, [currentSchema]); // currentSchema 의존성 추가

  // 스키마가 변경될 때마다 로컬 스토리지에 저장
  useEffect(() => {
    if (currentSchema && currentSchema.length > 0) {
      localStorage.setItem("documentSchema", JSON.stringify(currentSchema));
    }
  }, [currentSchema]);

  // 선택된 템플릿이 변경될 때마다 로컬 스토리지에 저장
  useEffect(() => {
    localStorage.setItem("selectedTemplate", selectedTemplate);
  }, [selectedTemplate]);

  // PDF가 닫힐 때 스키마와 템플릿 초기화 - 수정된 로직
  useEffect(() => {
    if (!pdfFile && !pdfDocument && !isDocumentLoaded) {
      // localStorage에서 스키마가 있는지 확인
      const savedSchema = localStorage.getItem("documentSchema");
      const savedTemplate = localStorage.getItem("selectedTemplate");

      // 저장된 스키마가 있으면 그대로 유지, 없으면 기본값 사용
      if (!savedSchema) {
        setCurrentSchema(DEFAULT_SCHEMA);
      }

      // 저장된 템플릿이 있으면 그대로 유지, 없으면 기본값 사용
      if (!savedTemplate) {
        setSelectedTemplate("user_defined");
      }
    }
  }, [pdfFile, pdfDocument, isDocumentLoaded]);

  // 템플릿 로드 시 스키마 변경
  const handleTemplateLoad = useCallback((templateName: string) => {
    if (templateLoadCallbackRef.current) {
      templateLoadCallbackRef.current(templateName);
    }
  }, []);

  // PDF 문서 닫힐 때 스키마 초기화
  const handleDocumentReset = useCallback(() => {
    const defaultSchema = DEFAULT_SCHEMA;
    setCurrentSchema(defaultSchema);
    setSelectedTemplate("user_defined");
    localStorage.removeItem("documentSchema");
    localStorage.setItem("selectedTemplate", "user_defined");

    // DocumentSchema에도 기본 스키마를 전달하여 동기화
    if (templateLoadCallbackRef.current) {
      // 기본 스키마를 localStorage에 저장하여 DocumentSchema가 로드할 수 있도록 함
      localStorage.setItem("documentSchema", JSON.stringify(defaultSchema));
    }
  }, []);

  // 스키마 변경 핸들러
  const handleSchemaChange = useCallback((newSchema: SchemaField[]) => {
    setCurrentSchema(newSchema);
  }, []);

  // 템플릿 변경 핸들러
  const handleTemplateChange = useCallback((templateName: string) => {
    setSelectedTemplate(templateName);
  }, []);

  // DocumentSchema에서 템플릿 로드 콜백을 받아서 저장
  const handleTemplateLoadCallback = useCallback(
    (callback: (templateName: string) => void) => {
      templateLoadCallbackRef.current = callback;
    },
    []
  );

  return (
    <Box h="100%" display="flex" flexDirection="column" overflow="hidden">
      <Flex gap={8} flex={1} p={8} minH={0} overflow="hidden">
        <Box flex={1} overflow="hidden" display="flex" flexDirection="column">
          <DocumentUploader
            onTemplateLoad={handleTemplateLoad}
            onReset={handleDocumentReset}
          />
        </Box>
        <Box
          flex={1}
          minH={0}
          display="flex"
          flexDirection="column"
          height="100%"
          overflow="hidden"
        >
          <DocumentSchema
            schema={currentSchema}
            onSchemaChange={handleSchemaChange}
            onTemplateLoad={handleTemplateLoadCallback}
            selectedTemplate={selectedTemplate}
            onTemplateChange={handleTemplateChange}
          />
        </Box>
      </Flex>
    </Box>
  );
};

export default ExtractSchema;
